// BL-177 Step 2d (section 7, LIVE BUG Oct 14 9:28 AM ET): one outbound text showed twice on screen. The thread had
// the just-sent (optimistic) bubble AND the saved row for the same text, because nothing tied them together once the
// the imported provider echo stamped the saved row with its webhook event id.
//
// The fix: every text sent from IntelAgent carries a client_ref, chosen in the browser before the send. The optimistic
// bubble, the saved raw_message_log row and the staff conversation_log entry all carry the same client_ref, and a
// message that arrives with a client_ref we already show REPLACES that bubble — it is never appended. This works in
// either order: the saved row can arrive (poll/realtime) before the send call returns, or after it.
//
// Pure functions only (no React, no Supabase) so both orderings unit test on their own.

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

/** The client_ref a message carries, whatever shape it arrived in (normalized entry, raw log row, optimistic entry). */
export function messageClientRef(message) {
    if (!message || typeof message !== 'object') return '';
    return firstString(message.clientRef, message.client_ref);
}

/** A new client_ref for a send. crypto.randomUUID where available; a timestamp+random fallback otherwise. */
export function newClientRef() {
    try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    } catch {
        // fall through
    }
    const random = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
    return `${random()}${random()}-${random()}-4${random().slice(1)}-a${random().slice(1)}-${random()}${random()}${random()}`;
}

function isEmpty(value) {
    return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

// Which copy of the same message leads when two are combined: a saved row beats an optimistic one; between two saved
// copies, the provider log row (it carries delivery status, the Retry ref and the imported provider source) leads.
function rank(message) {
    if (message?.pending) return 0;
    if (message?.source === 'raw_message_log') return 2;
    return 1;
}

/**
 * Combines two copies of the same message (same client_ref). The leading copy's values win; any value it lacks is
 * filled from the other copy (so the conversation_log role "staff" survives next to the raw row's delivery status).
 * The result is only "pending" when both copies are.
 */
export function combineSameMessage(existing, incoming) {
    const [lead, other] = rank(incoming) > rank(existing) ? [incoming, existing] : [existing, incoming];
    const combined = { ...other };
    Object.entries(lead).forEach(([key, value]) => {
        if (!isEmpty(value) || !(key in combined)) combined[key] = value;
    });
    combined.pending = Boolean(existing?.pending && incoming?.pending);
    // A sent text's media is the union of both copies (keys de-duplicated).
    const media = [...(Array.isArray(lead.media) ? lead.media : []), ...(Array.isArray(other.media) ? other.media : [])];
    if (media.length) {
        const seen = new Set();
        combined.media = media.filter((item) => {
            const key = item?.key || item?.url;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
        combined.mediaKeys = combined.media.map((item) => item.key).filter(Boolean);
        combined.mediaUrls = combined.media.map((item) => item.url).filter(Boolean);
    }
    return combined;
}

/**
 * Adds one message to a list. When the list already holds a message with the same client_ref, that message is
 * REPLACED in place by the combination of the two (never appended). Messages without a client_ref are appended as-is.
 * Returns a new array; the input list is not changed.
 */
export function reconcileMessages(list, incoming) {
    const current = Array.isArray(list) ? list : [];
    if (!incoming) return current.slice();
    const ref = messageClientRef(incoming);
    if (!ref) return [...current, incoming];
    const index = current.findIndex((message) => messageClientRef(message) === ref);
    if (index === -1) return [...current, incoming];
    const next = current.slice();
    next[index] = combineSameMessage(current[index], incoming);
    return next;
}

/** Folds many messages into a list with reconcileMessages, in order. */
export function reconcileAll(list, incoming = []) {
    return (Array.isArray(incoming) ? incoming : []).reduce(reconcileMessages, Array.isArray(list) ? list.slice() : []);
}

/**
 * Optimistic entries that are now saved can be dropped from local state: true when `saved` (raw rows / log entries)
 * already holds the entry's client_ref.
 */
export function isReconciled(pendingEntry, saved = []) {
    const ref = messageClientRef(pendingEntry);
    if (!ref) return false;
    return (Array.isArray(saved) ? saved : []).some((message) => messageClientRef(message) === ref);
}
